import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { baixarCSV } from '../utils';
import ColunaFiltros, { type SecaoFiltros } from './ColunaFiltros';
import ListaFiltrada from './ListaFiltrada';
import type { FiltroOpcao, FiltroPergunta, PessoaFiltrada } from '../types';

interface Props {
  eventoId: number;
  eventoNome: string;
  totalInscritos: number;
  // Quando há um chip de participação escolhido na lista, as perguntas e
  // contagens mostradas são só daquela participação. null = todas.
  inscricaoIds?: number[] | null;
}

function agrupar(linhas: FiltroOpcao[]): FiltroPergunta[] {
  const mapa = new Map<number, FiltroPergunta>();
  linhas.forEach((l) => {
    let p = mapa.get(l.pergunta_id);
    if (!p) {
      p = {
        pergunta_id: l.pergunta_id,
        ordem: Number(l.ordem),
        secao: l.secao,
        enunciado: l.enunciado,
        tipo: l.tipo,
        opcoes: [],
      };
      mapa.set(l.pergunta_id, p);
    }
    p.opcoes.push({ opcao: l.opcao, quantos: Number(l.quantos) });
  });
  return [...mapa.values()].sort((a, b) => a.ordem - b.ordem);
}

function agruparPorSecao(perguntas: FiltroPergunta[]): SecaoFiltros[] {
  const mapa = new Map<string, FiltroPergunta[]>();
  [...perguntas]
    .sort((a, b) => a.ordem - b.ordem)
    .forEach((p) => {
      const chave = p.secao ?? '';
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave)!.push(p);
    });
  return [...mapa.entries()].map(([secao, perguntasDaSecao]) => ({
    secao: secao || null,
    perguntas: perguntasDaSecao,
  }));
}

function filtrosAtivos(sel: Record<string, string[]>): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  Object.entries(sel).forEach(([chave, valores]) => {
    if (valores.length > 0) out[chave] = valores;
  });
  return out;
}

export default function AbaFiltrar({
  eventoId,
  eventoNome,
  totalInscritos,
  inscricaoIds = null,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [perguntas, setPerguntas] = useState<FiltroPergunta[]>([]);
  const [selecionados, setSelecionados] = useState<Record<string, string[]>>(() => {
    const f = searchParams.get('f');
    if (!f) return {};
    try {
      const parsed = JSON.parse(decodeURIComponent(f));
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  });
  const [abertos, setAbertos] = useState<Set<number>>(new Set());
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const [pessoas, setPessoas] = useState<PessoaFiltrada[]>([]);
  const [fotoUrlMap, setFotoUrlMap] = useState<Map<number, string>>(new Map());
  const [carregando, setCarregando] = useState(true);
  const [carregandoPessoas, setCarregandoPessoas] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState(false);
  const [gaveta, setGaveta] = useState(false);

  const chaveIds = useMemo(
    () => (inscricaoIds ? `i:${inscricaoIds.join(',')}` : 'todos'),
    [inscricaoIds],
  );

  // Carrega as perguntas filtráveis uma única vez.
  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setCarregando(true);
      setErro(null);
      const { data, error } = await supabase.rpc('filtros_do_evento', {
        p_evento_id: eventoId,
        p_inscricoes: inscricaoIds ?? null,
      });
      if (!ativo) return;
      if (error) {
        setErro(mensagemAmigavel(error));
        setCarregando(false);
        return;
      }
      const agrupadas = agrupar((data as FiltroOpcao[]) || []);
      setPerguntas(agrupadas);
      setAbertos(new Set(agrupadas.slice(0, 2).map((p) => p.pergunta_id)));
      setCarregando(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId, chaveIds]);

  // Busca a lista de inscritos conforme os filtros ativos.
  useEffect(() => {
    let ativo = true;
    async function carregarPessoas() {
      setCarregandoPessoas(true);
      const { data, error } = await supabase.rpc('inscritos_filtrados', {
        p_evento_id: eventoId,
        p_filtros: filtrosAtivos(selecionados),
      });
      if (!ativo) return;
      if (error) {
        setErro(mensagemAmigavel(error));
        setCarregandoPessoas(false);
        return;
      }
      setPessoas((data as PessoaFiltrada[]) || []);
      setCarregandoPessoas(false);
    }
    carregarPessoas();
    return () => {
      ativo = false;
    };
  }, [selecionados, eventoId]);

  // Mantém os filtros ativos na URL (?f=...) para compartilhar o link.
  useEffect(() => {
    const ativos = filtrosAtivos(selecionados);
    const next = new URLSearchParams(searchParams);
    if (Object.keys(ativos).length > 0) {
      next.set('f', encodeURIComponent(JSON.stringify(ativos)));
    } else {
      next.delete('f');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionados]);

  // Gera URLs assinadas para as fotos da lista filtrada.
  useEffect(() => {
    let ativo = true;
    const caminhos = [...new Set(pessoas.map((p) => p.foto).filter(Boolean))] as string[];
    if (caminhos.length === 0) {
      setFotoUrlMap(new Map());
      return;
    }
    (async () => {
      try {
        const { data } = await supabase.storage.from('fichas').createSignedUrls(caminhos, 3600);
        const urlPorCaminho = new Map<string, string>();
        (data || []).forEach((s) => {
          if (s.signedUrl) urlPorCaminho.set(s.path, s.signedUrl);
        });
        const mapa = new Map<number, string>();
        pessoas.forEach((p) => {
          if (p.foto && urlPorCaminho.has(p.foto) && !mapa.has(p.inscricao_id)) {
            mapa.set(p.inscricao_id, urlPorCaminho.get(p.foto)!);
          }
        });
        if (ativo) setFotoUrlMap(mapa);
      } catch {
        /* falha silenciosa: as fotos apenas não aparecem */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [pessoas]);

  const ativos = useMemo(() => {
    let n = 0;
    Object.values(selecionados).forEach((arr) => {
      n += arr.length;
    });
    return n;
  }, [selecionados]);

  const secoes = useMemo(() => agruparPorSecao(perguntas), [perguntas]);

  function aoAlternarOpcao(perguntaId: number, opcao: string) {
    setSelecionados((prev) => {
      const chave = String(perguntaId);
      const atual = prev[chave] ?? [];
      const nova = atual.includes(opcao)
        ? atual.filter((o) => o !== opcao)
        : [...atual, opcao];
      return { ...prev, [chave]: nova };
    });
  }

  function aoAlternarBloco(perguntaId: number) {
    setAbertos((prev) => {
      const novo = new Set(prev);
      if (novo.has(perguntaId)) novo.delete(perguntaId);
      else novo.add(perguntaId);
      return novo;
    });
  }

  function aoExpandir(perguntaId: number) {
    setExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(perguntaId)) novo.delete(perguntaId);
      else novo.add(perguntaId);
      return novo;
    });
  }

  function aoLimparTudo() {
    setSelecionados({});
  }

  async function baixarLista() {
    if (pessoas.length === 0) return;
    setBaixando(true);
    try {
      const cabecalho = ['Nome', 'Telefone', 'Cidade', 'Participação', 'Grupo', 'Equipe'];
      const linhas = pessoas.map((p) => [
        p.participante,
        p.telefone ?? '',
        p.cidade ?? '',
        p.participacao,
        p.grupo ?? '',
        p.equipe ?? '',
      ]);
      const slug = eventoNome.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      baixarCSV(`lista-filtrada-${slug}.csv`, [cabecalho, ...linhas]);
    } finally {
      setBaixando(false);
    }
  }

  if (carregando) {
    return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        <div className="hidden h-72 animate-pulse rounded-lg bg-secondary-100 lg:block" />
        <div className="h-72 animate-pulse rounded-lg bg-secondary-100" />
      </div>
    );
  }

  if (erro) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <p className="mt-1 text-sm text-secondary-500">Tente recarregar a página.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-6">
        <aside className="hidden w-[300px] shrink-0 lg:block">
          <div className="rounded-lg border border-secondary-200 bg-white p-4">
            <ColunaFiltros
              secoes={secoes}
              selecionados={selecionados}
              abertos={abertos}
              expandidos={expandidos}
              ativos={ativos}
              aoAlternarOpcao={aoAlternarOpcao}
              aoAlternarBloco={aoAlternarBloco}
              aoExpandir={aoExpandir}
              aoLimparTudo={aoLimparTudo}
            />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <ListaFiltrada
            pessoas={pessoas}
            fotoUrlMap={fotoUrlMap}
            total={totalInscritos}
            carregando={carregandoPessoas}
            baixando={baixando}
            temFiltros={ativos > 0}
            aoBaixar={baixarLista}
            aoLimpar={aoLimparTudo}
          />
        </div>
      </div>

      {/* Botão fixo no celular para abrir a gaveta de filtros. */}
      <button
        type="button"
        onClick={() => setGaveta(true)}
        className="fixed bottom-4 left-1/2 z-30 flex -translate-x-1/2 cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-primary-600 lg:hidden"
      >
        <i className="ri-filter-3-line text-base" />
        Filtrar ({ativos})
      </button>

      {/* Gaveta de filtros (celular). */}
      {gaveta && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Fechar filtros"
            onClick={() => setGaveta(false)}
            className="absolute inset-0 cursor-pointer bg-black/40"
          />
          <div className="absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-2xl bg-white animate-painel-entrar">
            <div className="flex items-center justify-between border-b border-secondary-100 px-4 py-3">
              <span className="text-base font-medium text-foreground-950">Filtros</span>
              <button
                type="button"
                onClick={() => setGaveta(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-secondary-500 transition-colors hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <ColunaFiltros
                secoes={secoes}
                selecionados={selecionados}
                abertos={abertos}
                expandidos={expandidos}
                ativos={ativos}
                aoAlternarOpcao={aoAlternarOpcao}
                aoAlternarBloco={aoAlternarBloco}
                aoExpandir={aoExpandir}
                aoLimparTudo={aoLimparTudo}
              />
            </div>
            <div className="border-t border-secondary-100 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
              <button
                type="button"
                onClick={() => setGaveta(false)}
                className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}